/**
 * Auth context: token storage in localStorage, login/logout, current user.
 * Sprint 5: minimal auth state for UI; full RBAC checks happen server-side.
 * Sprint 6: token key imported from tokenStorage (single source of truth).
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { TOKEN_KEY, USER_KEY } from "./tokenStorage";

export interface CurrentUser {
  id: number;
  username: string;
  full_name?: string;
  is_admin?: boolean;
  is_superuser?: boolean;
  is_active: boolean;
  roles: string[];
  permissions?: string[];
  tenant_id?: number;
  branch_id?: number | null;
  /** Backend authority flag: staff access is blocked until password rotation. */
  first_login_password_change_required?: boolean;
}

export interface LoginResult {
  ok: boolean;
  error?: string;
  mustChangePassword?: boolean;
}

interface AuthContextValue {
  token: string | null;
  user: CurrentUser | null;
  login: (username: string, password: string) => Promise<LoginResult>;
  logout: () => void;
  isReady: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [isReady, setIsReady] = useState(false);

  // Hydrate from localStorage
  useEffect(() => {
    const hydrate = async () => {
      const t = localStorage.getItem(TOKEN_KEY);
      const u = localStorage.getItem(USER_KEY);
      if (t) setToken(t);
      let cachedUser: CurrentUser | null = null;
      if (u) {
        try {
          cachedUser = JSON.parse(u);
          setUser({
            ...cachedUser!,
            is_admin: false,
            is_superuser: false,
            roles: [],
            permissions: [],
          });
        } catch {
          localStorage.removeItem(USER_KEY);
        }
      }
      if (t) {
        try {
          const me = await fetch("/api/v1/auth/me", {
            headers: { Authorization: `Bearer ${t}` },
          });
          if (me.ok) {
            const freshUser = await me.json();
            setUser(freshUser);
            localStorage.setItem(USER_KEY, JSON.stringify(freshUser));
          } else if (me.status === 401) {
            setToken(null);
            setUser(null);
            localStorage.removeItem(TOKEN_KEY);
            localStorage.removeItem(USER_KEY);
          } else if (cachedUser) {
            setUser({ ...cachedUser, is_admin: false, is_superuser: false, roles: [], permissions: [] });
          }
        } catch {
          if (cachedUser) {
            setUser({ ...cachedUser, is_admin: false, is_superuser: false, roles: [], permissions: [] });
          }
        }
      }
      setIsReady(true);
    };
    void hydrate();
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    try {
      const resp = await fetch("/api/v1/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      if (!resp.ok) {
        return { ok: false, error: `HTTP ${resp.status}` };
      }
      const data = await resp.json();
      if (!data.access_token) {
        return { ok: false, error: "no access_token" };
      }

      // Fetch /me
      const me = await fetch("/api/v1/auth/me", {
        headers: { Authorization: `Bearer ${data.access_token}` },
      });
      if (!me.ok) {
        return { ok: false, error: `Identity verification failed: HTTP ${me.status}` };
      }
      const meData = await me.json();
      // The login sentinel is the immediate signal, while /me is the durable
      // authority used for hydration and future route guards. Keep either true
      // value so a flagged first login never receives staff navigation.
      const mustChangePassword = Boolean(
        data.must_change_password || meData.first_login_password_change_required,
      );
      const currentUser: CurrentUser = {
        ...meData,
        first_login_password_change_required: mustChangePassword,
      };
      setToken(data.access_token);
      setUser(currentUser);
      localStorage.setItem(TOKEN_KEY, data.access_token);
      localStorage.setItem(USER_KEY, JSON.stringify(currentUser));
      return { ok: true, mustChangePassword };
    } catch (e: any) {
      return { ok: false, error: e?.message || "Network error" };
    }
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  }, []);

  const value = useMemo(
    () => ({ token, user, login, logout, isReady }),
    [token, user, login, logout, isReady]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
